import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import moment from "moment";
import NestedScreen from "../drawer/NestedScreen";
import {
  MAX_INTERVAL,
  REPEAT_PRESETS,
  RepeatRule,
  RepeatUnit,
  maxCount,
  maxUntil,
  minUntil,
  repeatEndError,
  ruleText,
} from "./repeatRule";
import styles from "./TimeBlockDrawer.module.css";

/** Interval / unit / occurrences: under the theme's 1rem input text (an
 * inline style, so it needs `styles`, not a class). */
const REPEAT_INPUT_STYLES = { input: { fontSize: "0.8rem", fontWeight: 500 } };

const UNITS: { value: RepeatUnit; label: string }[] = [
  { value: "Day", label: "days" },
  { value: "Week", label: "weeks" },
  { value: "Month", label: "months" },
];

interface RepeatScreenProps {
  /** "YYYY-MM-DD" of the first occurrence. */
  startDay: string;
  /** "YYYY-MM-DD" the first occurrence ends on (multi-day blocks); defaults
   * to `startDay`. "Ends on" dates must be after it. */
  endDay?: string;
  rule: RepeatRule;
  onBack: () => void;
  onDone: (rule: RepeatRule) => void;
  title?: string;
}

/**
 * The shared Repeat nested screen (time blocks now; appointment recurrence
 * later). Edits a draft; Done hands it back, Back drops it. Done refuses a
 * rule that ends on / before the block's end day: the visible Ends input
 * (date or occurrences) is marked invalid and focused.
 */
export default function RepeatScreen({
  startDay,
  endDay = startDay,
  rule,
  onBack,
  onDone,
  title = "Repeat",
}: RepeatScreenProps) {
  const [r, setR] = useState<RepeatRule>(rule);
  const [endError, setEndError] = useState<string>();
  const untilRef = useRef<HTMLInputElement>(null);
  const countRef = useRef<HTMLInputElement>(null);
  const set = (patch: Partial<RepeatRule>) => {
    setEndError(undefined);
    setR((x) => ({ ...x, ...patch }));
  };

  const done = () => {
    const err = repeatEndError(r, startDay, endDay);
    if (!err) {
      onDone(r);
      return;
    }
    setEndError(err);
    (r.endsType === "on" ? untilRef : countRef).current?.focus();
  };
  const repeating = r.preset !== "none";
  const cap = maxUntil(startDay);
  const countCap = maxCount(r, startDay);

  return (
    <NestedScreen
      title={title}
      onBack={onBack}
      footer={
        <>
          <Box style={{ flex: 1 }} />
          <Button variant="default" onClick={onBack}>
            Back
          </Button>
          <Button color="teal" onClick={done}>
            Done
          </Button>
        </>
      }
    >
      <Text className={styles.repeatStart}>
        Starting {moment(startDay, "YYYY-MM-DD").format("dddd, MMMM D")}
      </Text>
      <Box className={styles.radioGroup} role="radiogroup" aria-label="Repeat">
        {REPEAT_PRESETS.map((p) => {
          const on = r.preset === p.value;
          return (
            <UnstyledButton
              key={p.value}
              role="radio"
              aria-checked={on}
              data-on={on || undefined}
              className={styles.radioRow}
              onClick={() =>
                set(
                  p.value === "custom"
                    ? {
                        preset: "custom",
                        interval: r.interval > 1 ? r.interval : 3,
                      }
                    : { preset: p.value },
                )
              }
            >
              <Box component="span" className={styles.radioDot} />
              {p.label}
            </UnstyledButton>
          );
        })}
      </Box>

      {r.preset === "custom" && (
        <>
          <Text className={styles.groupLabel}>Custom</Text>
          <Group gap={8} wrap="nowrap" className={styles.customRow}>
            <Text size="sm">Every</Text>
            <NumberInput
              aria-label="Interval"
              w={72}
              styles={REPEAT_INPUT_STYLES}
              min={1}
              max={MAX_INTERVAL[r.unit]}
              clampBehavior="none"
              value={r.interval}
              onChange={(v) =>
                set({
                  interval: Math.max(
                    1,
                    Math.min(MAX_INTERVAL[r.unit], Number(v) || 1),
                  ),
                })
              }
            />
            <Select
              aria-label="Repeat unit"
              data={UNITS}
              styles={REPEAT_INPUT_STYLES}
              value={r.unit}
              allowDeselect={false}
              comboboxProps={{ withinPortal: false }}
              onChange={(v) => {
                const unit = (v as RepeatUnit) || "Week";
                set({
                  unit,
                  interval: Math.min(r.interval, MAX_INTERVAL[unit]),
                });
              }}
              style={{ flex: 1 }}
            />
          </Group>
        </>
      )}

      {repeating && (
        <>
          <Text className={styles.groupLabel}>Ends</Text>
          <Stack
            gap={0}
            className={styles.radioGroup}
            role="radiogroup"
            aria-label="Ends"
          >
            <Group
              wrap="nowrap"
              className={styles.radioRow}
              data-on={r.endsType === "after" || undefined}
            >
              <UnstyledButton
                role="radio"
                aria-checked={r.endsType === "after"}
                className={styles.radioButton}
                onClick={() => set({ endsType: "after" })}
              >
                <Box component="span" className={styles.radioDot} />
                After
              </UnstyledButton>
              {r.endsType === "after" && (
                <Group gap={8} wrap="nowrap">
                  <NumberInput
                    ref={countRef}
                    error={!!endError}
                    aria-label="Number of occurrences"
                    w={72}
                    styles={REPEAT_INPUT_STYLES}
                    min={2}
                    max={countCap}
                    clampBehavior="none"
                    value={Math.min(r.count, countCap)}
                    onChange={(v) =>
                      set({
                        count: Math.max(2, Math.min(countCap, Number(v) || 2)),
                      })
                    }
                  />
                  <Text size="sm">times</Text>
                </Group>
              )}
            </Group>
            <Group
              wrap="nowrap"
              className={styles.radioRow}
              data-on={r.endsType === "on" || undefined}
            >
              <UnstyledButton
                role="radio"
                aria-checked={r.endsType === "on"}
                className={styles.radioButton}
                onClick={() => set({ endsType: "on" })}
              >
                <Box component="span" className={styles.radioDot} />
                On a date
              </UnstyledButton>
              {r.endsType === "on" && (
                <TextInput
                  ref={untilRef}
                  error={!!endError}
                  type="date"
                  aria-label="End repeat on"
                  w={160}
                  min={minUntil(endDay)}
                  max={cap}
                  value={r.until}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    if (v) set({ until: v > cap ? cap : v });
                  }}
                />
              )}
            </Group>
          </Stack>
          {endError && (
            // Announced on appear; the input itself gets aria-invalid + focus.
            <Text role="alert" className={styles.error}>
              {endError}
            </Text>
          )}
          <Text className={styles.ruleSummary}>{ruleText(r, startDay)}</Text>
        </>
      )}
      <Text className={styles.limitNote}>
        Repeats can be scheduled up to 5 years out.
      </Text>
    </NestedScreen>
  );
}
